import { ASSETS } from '../content/market';
import { MILIEUX } from '../content/paths';
import { STARTING_SUBSCRIPTIONS } from '../content/subscriptions';
import { setCarCosts } from '../game/car';
import { addHousingFees, connectInternet } from '../game/housing';
import { round2 } from './finance';
import { t } from './i18n';
import { createInitialState, SAVE_VERSION, type GameState, type Subscription } from './state';
import { STORAGE_PREFIX, reprendreAnciennesCles } from './stockage';
import { addYearsISO, dateAtDay } from './time';

const BASE_SAVE_KEY = `${STORAGE_PREFIX}sauvegarde`;

/**
 * Emplacement de sauvegarde. `?partie=test` dans l'adresse utilise une
 * sauvegarde séparée, pour tester sans toucher à la partie principale.
 */
function saveKey(): string {
  const slot = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('partie') : null;
  return slot ? `${BASE_SAVE_KEY}.${slot}` : BASE_SAVE_KEY;
}

const SAVE_KEY = saveKey();
/** Une sauvegarde illisible est mise de côté ici, au lieu d'être écrasée par la partie neuve. */
const RESCUE_KEY = `${SAVE_KEY}.secours`;

export function saveGame(state: GameState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch {
    // Stockage indisponible (navigation privée, quota) : la partie continue sans sauvegarde.
  }
}

export function loadGame(): GameState | null {
  reprendreAnciennesCles();
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(SAVE_KEY);
    return raw ? migrate(JSON.parse(raw)) : null;
  } catch {
    // Une sauvegarde illisible (version plus récente, données abîmées) : on la met de côté avant que
    // l'autosauvegarde de la partie neuve ne l'écrase. La première mise de côté est gardée.
    try {
      if (raw && !localStorage.getItem(RESCUE_KEY)) localStorage.setItem(RESCUE_KEY, raw);
    } catch {
      // Stockage indisponible : rien de plus à faire.
    }
    return null;
  }
}

/** La sauvegarde illisible mise de côté, s'il y en a une. */
export function rescuedSave(): string | null {
  reprendreAnciennesCles();
  try {
    return localStorage.getItem(RESCUE_KEY);
  } catch {
    return null;
  }
}

export function forgetRescuedSave(): void {
  try {
    localStorage.removeItem(RESCUE_KEY);
  } catch {
    // Stockage indisponible.
  }
}

/**
 * Réessaie de lire la sauvegarde mise de côté (après une mise à jour du jeu qui la comprend, par exemple).
 * Si elle se charge, la copie de côté est effacée ; sinon, elle reste.
 */
export function retryRescuedSave(): GameState | null {
  const raw = rescuedSave();
  if (!raw) return null;
  try {
    const state = migrate(JSON.parse(raw));
    forgetRescuedSave();
    return state;
  } catch {
    return null;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    // Rien à effacer.
  }
}

/** Sauvegarde sous forme de texte à copier, compatible avec les accents. */
export function exportSave(state: GameState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

export function importSave(text: string): GameState {
  const binary = atob(text.trim());
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return migrate(JSON.parse(new TextDecoder().decode(bytes)));
}

/**
 * Convertit une sauvegarde vers la version actuelle. En version 1, on complète
 * simplement les champs ajoutés depuis avec leurs valeurs par défaut.
 */
/**
 * Les abonnements n'étaient qu'un total : on y retrouve ceux du départ (sans les deux oubliés si la
 * ligne a déjà été examinée) et la carte de cinéma ; ce qui reste devient « Autres abonnements ».
 */
function rebuildSubscriptions(state: GameState): Subscription[] {
  const expense = state.expenses.abonnements;
  if (!expense || expense.amount <= 0) return [];
  const list: Subscription[] = STARTING_SUBSCRIPTIONS.filter((sub) => !(expense.optimized && sub.forgotten)).map(
    (sub) => ({ ...sub, amount: round2(sub.amount * state.priceLevel) }),
  );
  if (state.perks.cinema) list.push({ id: 'cinema', amount: state.perks.cinema.leisure });
  const rest = round2(expense.amount - list.reduce((sum, sub) => sum + sub.amount, 0));
  // Des abonnements du départ ont déjà été résiliés : on ne sait plus lesquels.
  if (rest < 0) return [{ id: 'autres', amount: expense.amount }];
  if (rest >= 0.5) list.push({ id: 'autres', amount: rest });
  else if (rest > 0) list[0].amount = round2(list[0].amount + rest);
  return list;
}

export function migrate(raw: unknown): GameState {
  if (!raw || typeof raw !== 'object') throw new Error('Sauvegarde invalide');
  const data = raw as Partial<GameState>;
  if (typeof data.version !== 'number' || data.version > SAVE_VERSION) {
    throw new Error('Version de sauvegarde inconnue');
  }
  const defaults = createInitialState(data.createdAt ?? Date.now());
  const state: GameState = { ...defaults, ...data, version: SAVE_VERSION };
  // Le livret s'ouvre désormais avec le premier compte : les parties déjà
  // commencées au-delà de cette étape y ont droit aussi.
  if (state.stage !== 'prologue' || state.prologueStep === 'project' || state.prologueStep === 'years') {
    state.flags = { ...state.flags, livretOpen: true };
  }
  // Les parties d'avant la date de naissance commençaient le jour des 18 ans.
  if (!data.birthDate) state.birthDate = addYearsISO(state.startDate, -18);
  // Le livret devient un Livret A calculé par quinzaine : tout ce qui y est déjà rapporte tout de suite.
  if (data.livretBase === undefined) state.livretBase = state.livret;
  // Le niveau des prix n'existait pas : chaque bilan déjà passé a fait monter les prix de 2 %.
  if (data.priceLevel === undefined) state.priceLevel = Math.pow(1.02, state.yearsReviewed);
  if (data.subscriptions === undefined) state.subscriptions = rebuildSubscriptions(state);
  // Le versement programmé du PEA peut être suspendu : il ne l'est pas au départ.
  if (state.pea.monthlyPaused === undefined) state.pea = { ...state.pea, monthlyPaused: false };
  // Les indicateurs fondamentaux n'existaient pas : la valeur de fond part des cours connus.
  if (state.market.ema === undefined) {
    // Le cours de chaque fin de mois, lissé sur deux ans comme le fait le moteur jour après jour.
    const smooth = (prices: number[]) => {
      const out: number[] = [];
      for (const price of prices) out.push(out.length === 0 ? price : out[out.length - 1] + (price - out[out.length - 1]) / 24);
      return out;
    };
    const history = state.market.history;
    const emaHistory = Object.fromEntries(
      Object.entries(history).map(([id, prices]) => [id, smooth(prices).map((value) => round2(value))]),
    ) as typeof history;
    const ema = Object.fromEntries(
      Object.entries(emaHistory).map(([id, values]) => [id, values[values.length - 1]]),
    ) as typeof state.market.prices;
    const indexSmooth = smooth(state.market.indexHistory);
    state.market = {
      ...state.market,
      ema,
      emaHistory,
      indexEma: indexSmooth[indexSmooth.length - 1],
      profitable: {},
    };
  }
  // De nouveaux placements arrivent sur le marché : ils y entrent à leur prix de lancement, et leur courbe
  // commence ce jour-là.
  for (const asset of ASSETS) {
    if (state.market.prices[asset.id] !== undefined) continue;
    state.market = {
      ...state.market,
      prices: { ...state.market.prices, [asset.id]: asset.startPrice },
      history: { ...state.market.history, [asset.id]: [asset.startPrice] },
      ema: { ...state.market.ema, [asset.id]: asset.startPrice },
      emaHistory: { ...state.market.emaHistory, [asset.id]: [asset.startPrice] },
    };
  }
  // La courbe de l'immobilier n'existait pas : elle part du niveau des prix de la partie.
  if (state.market.realEstate === undefined) {
    const level = round2(100 * state.priceLevel);
    state.market = {
      ...state.market,
      realEstate: level,
      realEstateHistory: [level],
      realEstateSlump: 0,
    };
  }
  // Le taux du fonds euros n'existait pas : celui de 2025.
  if (state.market.eurosRate === undefined) state.market = { ...state.market, eurosRate: 0.026 };
  // La courbe du PEA n'existait pas : elle commence maintenant.
  if (state.pea.history === undefined) state.pea = { ...state.pea, history: [], historyWeek: -1 };
  // Pour l'instant, le jeu se met en pause pendant l'absence : plus de réserve de temps ni d'avance rapide.
  state.reserveDays = 0;
  state.speed = { ...state.speed, fastForward: false };
  state.offlineSummary = null;
  // La box internet n'était pas dans le budget : qui vit déjà hors de chez ses parents la reçoit, une fois.
  if (state.housing && state.housing !== 'parents' && !state.flags.boxInternet) connectInternet(state, state.housing);
  // Les charges et l'électricité n'existaient pas : qui vit déjà hors de chez ses parents les reçoit, une fois.
  addHousingFees(state);
  // Le droit au LEP regarde les revenus de l'an dernier : on les estime d'après le salaire du moment.
  if (data.lastTaxYearIncome === undefined && state.firstJobDay !== null) {
    state.lastTaxYearIncome = round2(state.salary * 12);
    state.taxYearIncome = round2(state.salary * dateAtDay(state.startDate, state.day).getMonth());
  }
  // L'augmentation compte les niveaux gagnés depuis la dernière augmentation : on part du dernier bilan,
  // ou des niveaux du moment. Les promotions comptent tous les niveaux déjà acquis (levelsAtHire reste à 0).
  if (data.levelsAtLastRaise === undefined) {
    const legacy = raw as { levelsAtLastReview?: number };
    state.levelsAtLastRaise =
      legacy.levelsAtLastReview ?? Object.values(state.skills).reduce((sum, skill) => sum + skill.level, 0);
  }
  delete (state as { levelsAtLastReview?: number }).levelsAtLastReview;
  // Les plaisirs du job d'été ne s'usent plus : leur compte n'a plus d'usage.
  delete (state as { summerTreatClicks?: number[] }).summerTreatClicks;
  // Les plaisirs s'usent désormais par catégorie : l'ancien compte, toutes catégories mêlées, repart de zéro.
  delete (state as { treatDays?: number[] }).treatDays;
  // La mutuelle santé n'existait pas : une formule de base, étudiante ou d'entreprise, au prix du moment.
  if (state.expenses.logement && !state.expenses.mutuelle) {
    const base = state.firstJobDay === null ? 30 : 60;
    state.expenses.mutuelle = { amount: Math.round(base * state.priceLevel), examined: 0, optimized: false };
  }
  // Les frais personnels n'existaient pas : vêtements, coiffeur et hygiène, au prix du moment.
  if (state.expenses.logement && !state.expenses.personnel) {
    const base = state.firstJobDay === null ? 50 : 90;
    state.expenses.personnel = { amount: Math.round(base * state.priceLevel), examined: 0, optimized: false };
  }
  // Les tarifs de la voiture ont changé : une voiture déjà achetée devient une occasion, à ses tarifs, une fois.
  if (state.hasCar && !state.flags.tarifsVoiture) {
    setCarCosts(state, 'used');
    state.flags = { ...state.flags, tarifsVoiture: true };
  }
  // Une voiture achetée avant qu'elle ait une valeur de revente : une estimation.
  if (state.hasCar && !data.carValue) {
    state.carValue = 4500;
    state.transportBeforeCar = 60;
  }
  // Le smartphone n'existait pas : un téléphone d'un an, comme en début de partie.
  if (!state.upgrades.includes('telephone') && !state.wornUpgrades.includes('telephone')) {
    state.upgrades = ['telephone', ...state.upgrades];
    state.upgradeDays = { ...state.upgradeDays, telephone: state.day - 365 };
  }
  // Les achats d'avant l'usure du matériel commencent leur durée de vie aujourd'hui.
  for (const id of state.upgrades) state.upgradeDays[id] ??= state.day;
  // Les chaussures de course ne s'usaient pas : celles déjà achetées commencent leur année aujourd'hui.
  if (!state.flags.usureCourse) {
    if (state.upgrades.includes('course')) state.upgradeDays = { ...state.upgradeDays, course: state.day };
    state.flags = { ...state.flags, usureCourse: true };
  }
  // Le milieu familial n'était écrit nulle part : une ligne au journal, juste avant le choix du chemin.
  const pathReached = state.stage !== 'prologue' || !['gift', 'summer', 'endSummer'].includes(state.prologueStep);
  if (pathReached && !state.journal.some((entry) => entry.key === 'journal.milieu')) {
    const pathIndex = state.journal.findIndex((entry) => entry.key.startsWith('journal.path.'));
    const entry = {
      day: pathIndex >= 0 ? state.journal[pathIndex].day : state.day,
      key: 'journal.milieu' as const,
      params: { milieu: t(MILIEUX[state.milieu].text) },
    };
    if (pathIndex >= 0) state.journal.splice(pathIndex, 0, entry);
    else state.journal.push(entry);
    if (state.journal.length > 300) state.journal.shift();
  }
  return state;
}
